import { ThemeProvider, PINField, Box } from '@ace-clublabs/shared-component-library';
import { useEffect, useState } from 'react';
import Header from '../components/Header';
import Spinner from '../components/Spinner';
import { useGlobalState } from '../shared/state';
import { sources } from '../api/sources';
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { Pages } from '../shared/pages';
import { ErrorType } from '../shared/types';
import { UtagHelper } from '../utils/utagHelper';
import LogCannon from '@ace-clublabs/log-cannon';
import { createZipCookieFromCode, checkExistingZipCookies, getDefaultZipForClub } from '../utils/createZipCookie';

export const PayByLinkAuthentication: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useGlobalState('error');
  const [customerId, setCustomerId] = useGlobalState('customerId');
  const [requestId, setEPCRequestId] = useGlobalState('epcRequestId');
  const [payByLinkData, setPayByLinkData] = useGlobalState('payByLinkData');
  const [, setManifestId] = useGlobalState('manifestId');
  const [sessionId] = useGlobalState('sessionId');

  const { customerId: customerIdFromParams, requestId: requestIdFromParams } = useParams<{
    customerId: string;
    requestId: string;
  }>();

  useEffect(() => {
    if (customerIdFromParams === customerId && requestIdFromParams === requestId && payByLinkData) {
      navigate(Pages.PayByLink);
    } else {
      setPayByLinkData(undefined);
      setError(undefined);
      setCustomerId(customerIdFromParams);
      setEPCRequestId(requestIdFromParams);
    }
  }, []);

  useEffect(() => {
    if (customerId && sessionId) {
      const navigationEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      const isBackNavigation = navigationEntries.length > 0 && navigationEntries[0].type === 'back_forward';

      const smsVerificationTags = {
        channel: 'my account',
        subchannel1: 'sms',
        page: 'my account:sms:verification code',
        sessionid: sessionId,
        custID: customerId,
      };

      if (isBackNavigation) {
        UtagHelper.tag(smsVerificationTags);
      } else {
        (global as any).aceMediaTagValues = { ...smsVerificationTags };

        if (typeof (global as any).utag === 'undefined') {
          UtagHelper.loadUtagJs();
        } else {
          (global as any).utag.view(smsVerificationTags);
        }
      }
    }
  }, [customerId, sessionId]);

  const handleSubmit = async (pin: string) => {
    try {
      setLoading(true);
      const getPBLDataRequest = { requestId: requestId || '', custId: customerId || '', pin };
      const getPBLDataResponse = await sources.getPBLData(getPBLDataRequest);

      const { manifestId: _manifestId, clubCode } = getPBLDataResponse;
      if (getPBLDataResponse && _manifestId) {
        setManifestId(_manifestId);
        setPayByLinkData(getPBLDataResponse);

        // Create zip cookie if not already exists
        const existingCookies = checkExistingZipCookies();
        if (!existingCookies.exists && clubCode) {
          try {
            const defaultZip = getDefaultZipForClub(clubCode);
            if (defaultZip) {
              await createZipCookieFromCode(defaultZip);
              LogCannon.blast('PBL: Created zip cookie', { clubCode, zipCode: defaultZip });
            }
          } catch (zipError: any) {
            LogCannon.blast('PBL: Failed to create zip cookie', zipError);
          }
        }

        navigate(Pages.PayByLink);
      }
      setLoading(false);
    } catch (err: any) {
      if (err.type === ErrorType.PBL_INCORRECT_PIN) {
        setLoading(false);
        setError({
          errorMessage: 'Verification failed. Please try inputting your code again.',
          errorType: ErrorType.PBL_INCORRECT_PIN,
        });
        UtagHelper.link({
          channel: 'my account',
          custID: customerId,
          error: 'my account:sms:verification code-verification failed',
          page: 'my account:sms:verification code',
          sessionid: sessionId,
          subchannel1: 'sms',
        });
        const errorMessage = 'ERROR: Incorrect PBL PIN';
        LogCannon.blast(errorMessage);
      } else if (err.type === ErrorType.PBL_TOO_MANY_ATTEMPTS) {
        setLoading(false);
        setError({
          errorMessage: "You've entered the wrong code too many times. Please request a new link from your agent.",
          errorType: ErrorType.PBL_TOO_MANY_ATTEMPTS,
        });
        UtagHelper.link({
          channel: 'my account',
          custID: customerId,
          error: 'my account:sms:verification code-too many attempts',
          page: 'my account:sms:verification code',
          sessionid: sessionId,
          subchannel1: 'sms',
        });
        const errorMessage = 'ERROR: Too Many Attempts for PBL Verification';
        LogCannon.blast(errorMessage);
      } else if (err.type === ErrorType.PBL_EXPIRED) {
        setError({ errorMessage: '', errorType: ErrorType.PBL_EXPIRED });
        const errorMessage = 'ERROR: PBL Link Expired';
        LogCannon.blast(errorMessage, err);
        navigate(Pages.Error);
      } else {
        setError({ errorMessage: '', errorType: ErrorType.PBL_ERROR });
        const errorMessage = 'ERROR: Fail to Fetch PBL Data';
        LogCannon.blast(errorMessage, err);
        navigate(Pages.Error);
      }
    }
  };

  useEffect(() => {
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        window.location.reload();
      }
    });

    return () => {
      window.removeEventListener('pageshow', () => {});
    };
  }, []);

  return (
    <>
      {isLoading && <Spinner />}
      <div id="pay-by-link-authentication" className="inner-container" hidden={isLoading}>
        <Header />
        <ThemeProvider>
          <Box id="pbl-pin">
            <PINField
              title="Enter your verification code"
              subtitle="Your agent will provide you with a 4-digit verification code. Your payment link will expire after 1 hour."
              onSubmit={handleSubmit}
              errorMessage={error?.errorMessage}
              disabled={error?.errorType === ErrorType.PBL_TOO_MANY_ATTEMPTS ? true : false}
            />
          </Box>
        </ThemeProvider>
      </div>
    </>
  );
};
